import React, { useState, useMemo, useEffect } from 'react';
import moment from 'moment';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { useSelector } from 'react-redux';
import Select, { components } from 'react-select';
import { faTimes, faSearch, faTrashAlt, faFileExcel } from '@fortawesome/fontawesome-free-solid';
import { Fa } from '../../elements/fa';
import { ReactComponent as CheckboxIcon } from '../../../assets/svg/check_box_16w.svg';
import { ReactComponent as CheckboxBlankIcon } from '../../../assets/svg/check_box_outline_blank_16w.svg';
import { iNote, iList } from '../../../shared/interfaces';
import { vals, compareStringsAlphabetically } from '../../../shared/helpers';
import NoteItem from './NoteItem';
import CustomDatePicker from '../../LoanerAssignments/components/CustomDatePicker';
import styles from './Notepad.module.scss';
import * as notesdb from '../../../shared/db/notes-db';
import { commonSelectStyles, commonSelectTheme } from '../selections-grid/constants';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

interface NoteSearchModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentNotes: iList<iNote>;
  deviceOrFence: 'device' | 'fence';
  deviceOrFenceId: string;
  ownerTitle?: string;
  globalVisibleFields: string[];
  noteFieldDefs: any;
  defaultToAllVehiclesOnOpen?: boolean;
}

const SCOPE_OPTIONS = [
  { value: 'current', label: 'Current Vehicle Only' },
  { value: 'all', label: 'All Vehicles' },
  { value: 'specific', label: 'Select Specific Vehicles...' }
];

const CheckboxOption = (props: any) => {
  return (
    <components.Option   {...props}>
      <div style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', margin: 0, padding: 0, lineHeight: '16px', gap: 8 }}>
        {props.isSelected
          ? <CheckboxIcon style={{ width: 16, height: 16, flexShrink: 0 }} />
          : <CheckboxBlankIcon style={{ width: 16, height: 16, flexShrink: 0, color: '#b9b9b9' }} />
        }
        <label style={{ margin: 0, cursor: 'pointer', fontSize: 10, lineHeight: '16px', color: '#878889' }}>{props.label}</label>
      </div>
    </components.Option>
  );
};

const NoteSearchModal = ({
  isOpen,
  onClose,
  currentNotes,
  deviceOrFence,
  deviceOrFenceId,
  ownerTitle,
  globalVisibleFields,
  noteFieldDefs,
  defaultToAllVehiclesOnOpen = false
}: NoteSearchModalProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [searchScope, setSearchScope] = useState(SCOPE_OPTIONS[0]);
  const [selectedItems, setSelectedItems] = useState<any[]>([]);
  const [fetchedNotes, setFetchedNotes] = useState<iNote[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [openNoteId, setOpenNoteId] = useState<string | null>(null);
  const [fieldOrder, setFieldOrder] = useState<string[]>([]);
  const rawDevices = useSelector((state: any) => state.devicesData?.devicesDetails);

  const itemOptions = useMemo(() => {
    let itemsArray: any[] = [];

    if (rawDevices) {
      if (typeof rawDevices.valueSeq === 'function') {
        itemsArray = rawDevices.valueSeq().toArray();
      } else {
        itemsArray = Object.values(rawDevices);
      }
    }

    return itemsArray
      .filter((item: any) => item && !item.deleted && item.name)
      .map((item: any) => ({
        value: String(item.id),
        label: item.name || item.title || `Item ${item.id}`
      }))
      .sort((a, b) => compareStringsAlphabetically(a.label, b.label));
  }, [rawDevices]);

  const deviceNameById = useMemo(() => {
    return itemOptions.reduce((acc, item) => {
      acc[String(item.value)] = item.label;
      return acc;
    }, {} as Record<string, string>);
  }, [itemOptions]);

  const specificVehiclesSelectStyles = useMemo(() => ({
    ...commonSelectStyles,
    control: (base: any) => {
      const commonControl = commonSelectStyles.control ? commonSelectStyles.control(base) : base;
      return {
        ...commonControl,
        minHeight: 24,
        height: 24,
        fontSize: 10,
        alignItems: 'center',
      };
    },
    valueContainer: (base: any) => {
      const commonValueContainer = commonSelectStyles.valueContainer ? commonSelectStyles.valueContainer(base) : base;
      return {
        ...commonValueContainer,
        fontSize: 10,
        minHeight: 22,
        paddingTop: 0,
        paddingBottom: 0,
      };
    },
    input: (base: any) => {
      const commonInput = commonSelectStyles.input ? commonSelectStyles.input(base) : base;
      return {
        ...commonInput,
        fontSize: 10,
      };
    },
    singleValue: (base: any) => {
      const commonSingleValue = commonSelectStyles.singleValue ? commonSelectStyles.singleValue(base) : base;
      return {
        ...commonSingleValue,
        fontSize: 10,
        lineHeight: '16px',
      };
    },
    placeholder: (base: any) => {
      const commonPlaceholder = commonSelectStyles.placeholder ? commonSelectStyles.placeholder(base) : base;
      return {
        ...commonPlaceholder,
        fontSize: 10,
        lineHeight: '16px',
      };
    },
    indicatorsContainer: (base: any) => ({
      ...base,
      height: 22,
      alignItems: 'center',
    }),
    dropdownIndicator: (base: any) => ({
      ...base,
      paddingTop: 0,
      paddingBottom: 0,
    }),
    menu: (base: any) => {
      const commonMenu = commonSelectStyles.menu ? commonSelectStyles.menu(base) : base;
      return {
        ...commonMenu,
        width: '100%',
        fontSize: 10,
      };
    },
    menuList: (base: any) => {
      return {
        ...base,
        display: 'grid',
        gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
        columnGap: '20px',
        rowGap: '1px',
        padding: '12px',
        maxHeight: '360px',
      };
    },
    option: (base: any, state: any) => {
      const commonOption = commonSelectStyles.option ? commonSelectStyles.option(base) : base;
      return {
        ...commonOption,
        margin: 0,
        padding: 0,
        fontSize: '10px',
        minHeight: '16px',
        lineHeight: '16px',
      };
    },
  }), []);

  useEffect(() => {
    const fetchOrder = async () => {
      try {
        const savedOrder = await notesdb.getFieldOrder();
        const DEFAULT_ORDER = ['note', 'po', 'techName', 'completedDate', 'amount', 'complete', 'file', 'date'];

        let finalOrder = DEFAULT_ORDER;
        if (savedOrder && Array.isArray(savedOrder) && savedOrder.length > 0) {
          finalOrder = [
            ...savedOrder,
            ...DEFAULT_ORDER.filter(item => !savedOrder.includes(item))
          ];
        }

        const extraKeys = Object.keys(noteFieldDefs || {});
        if (extraKeys.length > 0) {
           finalOrder = [
             ...finalOrder,
             ...extraKeys.filter(id => !finalOrder.includes(id))
           ];
        }

        setFieldOrder(finalOrder);
      } catch (e) {
        console.error("Failed to load field order", e);
      }
    };

    if (isOpen) {
      fetchOrder();
    }
  }, [isOpen, noteFieldDefs]);

  useEffect(() => {
    if (searchScope.value === 'current') {
        setFetchedNotes([]);
        return;
    }

    if (searchScope.value === 'specific' && selectedItems.length === 0) {
        setFetchedNotes([]);
        return;
    }

    const loadData = async () => {
      setIsLoading(true);
      try {
        let results: iNote[] = [];

        if (searchScope.value === 'all') {
          results = await notesdb.getAllNotesByCategory(deviceOrFence);
        }
        else if (searchScope.value === 'specific') {
          const ids = selectedItems.map(opt => String(opt.value));
          results = await notesdb.getNotesForMultipleItems(deviceOrFence, ids);
        }

        setFetchedNotes(results);
      } catch (err) {
        console.error("Error searching notes:", err);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [searchScope, selectedItems, deviceOrFence]);

  useEffect(() => {
    if (!isOpen) return;
    setSearchScope(defaultToAllVehiclesOnOpen ? SCOPE_OPTIONS[1] : SCOPE_OPTIONS[0]);
    setSelectedItems([]);
  }, [isOpen, defaultToAllVehiclesOnOpen]);

  const clearDateFilters = () => {
    setStartDate('');
    setEndDate('');
  };

  const filteredNotes = useMemo(() => {
    let arr: iNote[] = [];

    if (searchScope.value === 'current') {
      arr = vals(currentNotes);
    } else {
      arr = fetchedNotes;
    }

    const DATE_FORMAT = 'MM-DD-YYYY';
    if (startDate || endDate) {
      arr = arr.filter(note => {
        if (!note.date) return false;
        const noteDate = moment(note.date);
        const start = startDate ? moment(startDate, DATE_FORMAT).startOf('day') : null;
        const end = endDate ? moment(endDate, DATE_FORMAT).endOf('day') : null;

        if (start && end) return noteDate.isBetween(start, end, 'day', '[]');
        if (start) return noteDate.isSameOrAfter(start);
        if (end) return noteDate.isSameOrBefore(end);
        return true;
      });
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      arr = arr.filter(note => {
        const baseContent = [
          note.note,
          note.po,
          note.techName,
          note.amount != null ? String(note.amount) : '',
          note.date ? moment(note.date).format('MM/DD/YYYY') : '',
          note.completedDate || ''
        ];

        const extraContent = note.extraValues
          ? Object.values(note.extraValues).map(v => {
              if (typeof v.value === 'boolean') return v.value ? 'yes' : 'no';
              if (v.value && typeof v.value === 'object' && 'fileName' in (v.value as any)) return (v.value as any).fileName;
              return String(v.value || '');
            })
          : [];

        const allText = [...baseContent, ...extraContent].join(' ').toLowerCase();
        return allText.includes(q);
      });
    }

    return arr.sort((a, b) => {
       const timeA = a.date ? moment(a.date).valueOf() : 0;
       const timeB = b.date ? moment(b.date).valueOf() : 0;
       return timeB - timeA;
    });
  }, [currentNotes, fetchedNotes, searchScope, searchQuery, startDate, endDate]);

  const totalSpent = useMemo(() => {
    const toNumberSafe = (v: any) => (Number.isFinite(Number(v)) ? Number(v) : 0);
    return filteredNotes.reduce((sum, note) => sum + toNumberSafe(note?.amount), 0);
  }, [filteredNotes]);

  const handleExport = async () => {
    if (!filteredNotes.length) return;

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Notes Export');

    const activeOrder = fieldOrder.length > 0
      ? fieldOrder
      : ['note', 'po', 'techName', 'completedDate', 'amount', 'complete', 'date'];

    const exportOrder = activeOrder.filter(id => id !== 'file');

    const formatFallbackFieldLabel = (fieldId: string) => {
      return fieldId
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        .replace(/[_-]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
        .replace(/\b\w/g, (ch) => ch.toUpperCase());
    };

    const getColumnDef = (fieldId: string) => {
      switch (fieldId) {
        case 'date': return { header: 'Date', key: 'date', width: 15 };
        case 'note': return { header: 'Note', key: 'note', width: 40 };
        case 'po': return { header: 'PO', key: 'po', width: 15 };
        case 'techName': return { header: 'Tech Name', key: 'techName', width: 20 };
        case 'amount': return { header: 'Amount', key: 'amount', width: 12 };
        case 'completedDate': return { header: 'Completed Date', key: 'completedDate', width: 15 };
        case 'complete': return { header: 'Complete', key: 'complete', width: 10 };
        default: return {
          header: noteFieldDefs[fieldId]?.details?.name || formatFallbackFieldLabel(fieldId),
          key: fieldId,
          width: 20,
        };
      }
    };

    const columns = exportOrder.map(getColumnDef);
    worksheet.columns = columns;

    filteredNotes.forEach(note => {
      const rowData: any = {};

      exportOrder.forEach(fieldId => {
        switch (fieldId) {
          case 'date':
            rowData.date = note.date ? moment(note.date).format('MM/DD/YYYY') : '';
            break;
          case 'note': rowData.note = note.note || ''; break;
          case 'po': rowData.po = note.po || ''; break;
          case 'techName': rowData.techName = note.techName || ''; break;
          case 'amount': rowData.amount = note.amount ?? 0; break;
          case 'completedDate': rowData.completedDate = note.completedDate || ''; break;
          case 'complete': rowData.complete = note.complete ? 'Yes' : 'No'; break;
          default:
            const val = note.extraValues?.[fieldId]?.value;
            let strVal = '';
            if (typeof val === 'boolean') strVal = val ? 'Yes' : 'No';
            else if (val && typeof val === 'object' && 'fileName' in val) strVal = (val as any).fileName;
            else strVal = String(val || '');
            rowData[fieldId] = strVal;
            break;
        }
      });

      const row = worksheet.addRow(rowData);

      row.eachCell({ includeEmpty: true }, (cell) => {
        cell.font = { name: 'Calibri', size: 10 };
        cell.alignment = { vertical: 'middle', horizontal: 'left', wrapText: true };
      });
    });

    worksheet.getRow(1).eachCell((cell) => {
      cell.font = { name: 'Calibri', size: 11, bold: true };
      cell.alignment = { vertical: 'middle', horizontal: 'center' };
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(blob, `notes_export_${moment().format('YYYY-MM-DD_HH-mm')}.xlsx`);
  };

  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.searchModalContent}>

        <div className={styles.modalHeader}>
          <h3>Search Notes</h3>
          <button onClick={onClose} className={styles.closeBtn}>
            <Fa icon={faTimes} />
          </button>
        </div>

        <div className={styles.searchControls}>
          <div className={styles.controlRow}>
            <div className={styles.scopeRadioGroup}>
              {SCOPE_OPTIONS.map((option) => (
                <label key={option.value} className={styles.scopeRadioLabel}>
                  <input
                    type="radio"
                    name="note-search-scope"
                    value={option.value}
                    checked={searchScope.value === option.value}
                    onChange={() => {
                      setSearchScope(option);
                      if (option.value !== 'specific') setSelectedItems([]);
                    }}
                  />
                  <span>{option.label}</span>
                </label>
              ))}
            </div>
          </div>

          {searchScope.value === 'specific' && (
            <div className={styles.controlRow}>
              <div>
                <Select
                  isMulti
                  value={selectedItems}
                  options={itemOptions}
                  onChange={(val: any) => setSelectedItems(val)}
                  components={{ Option: CheckboxOption }}
                  hideSelectedOptions={false}
                  className={styles.reactSelectContainer}
                  classNamePrefix="react-select"
                  placeholder={`Select specific vehicles...`}
                  closeMenuOnSelect={false}
                  menuPortalTarget={document.body}
                  styles={specificVehiclesSelectStyles}
                  theme={commonSelectTheme}
                />
              </div>
            </div>
          )}

          <div className={styles.searchInputWrapperModal}>
            <Fa icon={faSearch} className={styles.searchIcon} />
            <input
              type="text"
              autoFocus
              placeholder="Search text, PO, Tech Name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={styles.searchInput}
            />
            <div className={styles.dateFilters}>
              <CustomDatePicker value={startDate} onChange={setStartDate} />
              <span className={styles.dash}>—</span>
              <CustomDatePicker value={endDate} onChange={setEndDate} />

              {(startDate || endDate) && (
                <button
                  className={styles.clearDateBtn}
                  onClick={clearDateFilters}
                  title="Clear dates"
                  style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#666', display: 'flex', alignItems: 'center', marginLeft: 10 }}
                >
                  <Fa icon={faTrashAlt} />
                </button>
              )}
            </div>
          </div>
        </div>

        <div className={styles.resultsArea}>
          {isLoading ? (
            <div className={styles.loading}>
              Loading notes from database...
            </div>
          ) : filteredNotes.length === 0 ? (
            <div className={styles.emptyState}>
              {searchScope.value === 'specific' && selectedItems.length === 0
                ? 'Please select at least one vehicle.'
                : 'No notes found matching your criteria.'}
            </div>
          ) : (
            <PerfectScrollbar>
              {filteredNotes.map((note) => (
                <NoteItem
                  key={note.id}
                  note={note}
                  isOpen={openNoteId === note.id}
                  onToggle={(id) => setOpenNoteId(id === openNoteId ? null : id)}
                  deviceOrFence={deviceOrFence}
                  deviceOrFenceId={(note as any).parentId || deviceOrFenceId}
                  editLinkState={{ state: { ownerTitle } }}
                  disabled={false}
                  globalVisibleFields={globalVisibleFields}
                  noteFieldDefs={noteFieldDefs}
                  showDeviceName={searchScope.value === 'all'}
                  deviceName={deviceNameById[String((note as any).parentId || deviceOrFenceId)] || ''}
                />
              ))}
            </PerfectScrollbar>
          )}
        </div>

        <div className={styles.modalFooter}>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <div className={styles.statItem}>Found: <b>{filteredNotes.length}</b></div>
            <div className={styles.statItem}>Total: <b>${totalSpent.toFixed(2)}</b></div>
          </div>

          {filteredNotes.length > 0 && (
            <button
              onClick={handleExport}
              className={styles.exportBtn}
            >
              <Fa icon={faFileExcel} /> Export
            </button>
          )}
        </div>

      </div>
    </div>
  );
};

export default NoteSearchModal;